/**
 * NetSession: the transport-agnostic multiplayer state machine.
 *
 * Every peer is either the elected host or a client that is also a hot
 * standby (it holds the replicated mobs, so it can take over). See
 * docs/p2p-multiplayer-design.md.
 *
 * Two world models, chosen once per session:
 *   - server mode (`opts.link`): the room's world server orders and validates
 *     every block edit (docs/world-server.md). The host only runs the
 *     simulation; its block changes are requests like anyone else's, and the
 *     P2P edit log below is never used.
 *   - P2P mode (no world server configured): the host is the world's
 *     authority and orders edits in its own log.
 *
 * The game is reached only through `GameAdapter`, so the whole protocol runs
 * in node against a fake world (smoke/net.ts) over a LoopbackHub.
 */

import {
  K_BLOB, K_CTRL, K_ENTS, K_HBEAT, K_PLAYERS, K_PONG, K_PSTATE, PF_DEAD,
  decodeBlobPart, decodeCtrl, decodeEntities, decodeHeartbeat, decodePlayers, decodePong, decodePState,
  deflate, encodeBlobParts, encodeCtrl, encodeEntities, encodeHeartbeat, encodePlayers, encodePong, encodePState,
  fromUtf8, inflate, packQuads, unpackQuads, utf8, Reader, Writer,
  stackExtras, stackWith, type Ctrl, type EntitiesMsg, type EntityState, type PlayerState,
} from './codec';
import type { Stack } from '../game/types';
import type { MobUseFx } from '../entities/mobUse';
import { EditLog, divergenceSeq, fromTuple, toTuple, type Bounds, type Edit } from './editlog';
import type { BlockRow, WorldLink } from './worldServer';
import { acceptClaim, freeSlot, hostOrder, nextEpoch, rankOf, successor, type Member } from './election';
import { fromB64, idFromPub, sha256, toB64, verify, type Identity } from './identity';
import type { Channel, Transport } from './transport';
import { RateLimiter, Violations, plausibleMove, sanitizeChat, sanitizeName, withinReach } from './validate';
import { INTERP_DELAY_MS, SnapshotBuffer } from './interp';

export type Role = 'host' | 'client';
export type Status = 'connecting' | 'syncing' | 'live' | 'migrating' | 'closed';

export type { MobUseFx } from '../entities/mobUse';

export interface GameAdapter {
  readonly seed: number;
  readonly worldgenVersion: number;
  /** Current block id, or -1 when unknown (chunk not loaded). */
  getBlock(x: number, y: number, z: number): number;
  /** Apply an authoritative/remote edit (no echo back into the session). */
  setBlockRemote(x: number, y: number, z: number, id: number): void;
  /** Whole world delta as (cx, cz, index, id) quads. */
  exportDelta(): Int32Array;
  importDelta(q: Int32Array): void;
  localState(tick: number): PlayerState;
  teleport(x: number, y: number, z: number): void;
  /** Did this client already have a saved position in this world? */
  hasSavedPosition(): boolean;
  time(): number;
  setTime(t: number): void;
  /** Host: mobs to replicate. */
  mobStates(): EntityState[];
  mobPos(id: number): [number, number, number] | null;
  /** Host: another player's position, by peer id (the local one included). */
  playerPos(id: string): [number, number, number] | null;
  hitMob(id: number, dmg: number, fromX: number, fromZ: number, kb: number): boolean;
  hitPlayer(id: string, dmg: number, fromX: number, fromZ: number, kb: number): boolean;
  /** Host: a client right-clicked a mob holding `held`; null = nothing happened. */
  useMob?(id: number, held: Stack): MobUseFx | null;
  /** Client: replicated mob state arrived. */
  applyEntities(msg: EntitiesMsg, now: number): void;
  /** true while a client (mobs come from the network), false while host. */
  setReplica(on: boolean): void;
  /** true while the local player is parked, frozen, waiting for its spawn chunk. */
  holding?(): boolean;
  /** Server mode: the dimension the local player is in (0 overworld, 1 nether). */
  dim?(): number;
  /** Server mode: an authoritative block value in any dimension (no echo). */
  setBlockIn?(dim: number, x: number, y: number, z: number, id: number): void;
  /** Server mode: the room's persistent world flags (the sim role reports them). */
  worldFlags?(): string[];
  addWorldFlag?(f: string): void;
}

export interface SessionEvents {
  chat?(name: string, text: string, system: boolean): void;
  members?(): void;
  status?(s: Status, detail?: string): void;
  hostChanged?(hostId: string, term: number): void;
  playerLeft?(id: string): void;
  kicked?(reason: string): void;
  /** Client: the host ran our right-click on a mob (`r` from useMob). */
  mobUsed?(r: number, fx: MobUseFx): void;
}

export interface SessionOptions {
  name: string;
  code: string;
  /** true: create the room (become host, term 1). */
  create: boolean;
  worldId?: string;
  /** Re-hosting a cached room: continue its edit log after (lastSeq, lastTerm). */
  resume?: { lastSeq: number; lastTerm: number; bounds: Bounds };
  hostTimeoutMs?: number;
  /** Auto mode (shared world): join if a host answers within this many ms, else host alone. */
  discoverMs?: number;
  heartbeatMs?: number;
  now?: () => number;
  /** The page's link to the room's world server; absent = P2P world model. */
  link?: WorldLink | null;
}

interface RemotePeer {
  id: string;
  name: string;
  pub: Uint8Array;
  tid: string;
  lastN: number;
  sid: string;
  lastHeard: number;
  // host-side validation
  accepted: PlayerState | null;
  acceptedAt: number;
  edits: RateLimiter;
  breaks: RateLimiter;
  chats: RateLimiter;
  hits: RateLimiter;
  renames: RateLimiter;
  violations: Violations;
  lastCorrection: number;
  ping: number;
  // per-peer entity delta state (host)
  entSig: Map<number, number>;
  entTick: number;
  // rendering
  buf: SnapshotBuffer;
  latest: PlayerState | null;
  /** When `latest` was last refreshed (the hurt flash expires off this). */
  latestAt: number;
}

interface PendingEdit { r: number; x: number; y: number; z: number; id: number; prev: number; t: number }

/** Continuous fields of a remote player snapshot: x y z yaw headYaw pitch limbSwing limbAmt swing */
export const PLAYER_INTERP_WIDTH = 9;
const PLAYER_ANGLES = [3, 4];

export interface RemotePlayerView { id: string; name: string; state: PlayerState; v: Float64Array; ping: number; isHost: boolean }

export class NetSession {
  role: Role;
  status: Status = 'connecting';
  term = 0;
  hostId = '';
  worldId: string;
  readonly members = new Map<string, Member>();
  readonly log = new EditLog();
  readonly peers = new Map<string, RemotePeer>();
  /** transport id -> identity id */
  private readonly tToId = new Map<string, string>();
  private readonly pending = new Map<number, PendingEdit>();
  private reqSeq = 1;
  private outN = 0;
  private useSeq = 0;
  /** Random per-session id: a reloaded tab restarts its counter under a new sid. */
  private readonly sid = Math.random().toString(36).slice(2, 10);
  private outQ: Promise<void> = Promise.resolve();
  private readonly inQ = new Map<string, Promise<void>>();
  private tickN = 0;
  private batch: Edit[] = [];
  private acks = new Map<string, number[]>();
  private bufferedEdits: Edit[] = [];
  private blob: { id: number; n: number; parts: Uint8Array[]; got: number; meta: Ctrl } | null = null;
  private blobSeq = 1;
  private hostSilentSince = 0;
  private resyncing = false;
  private readonly startedAt: number;
  private suspects = new Set<string>();
  private lastLogReq = 0;
  private lastPings = 0;
  private lastAlive = 0;
  /** A rename the host has not confirmed yet (see tickRename). */
  private nameDirty = false;
  private renameAt = 0;
  private renameTries = 0;
  private readonly hostTimeout: number;
  private readonly hbEvery: number;
  private lastHb = 0;
  private timer: (() => void) | null = null;
  readonly now: () => number;
  private selfMember: Member;
  myPing = 0;
  /** The room's world server, when this session runs in server mode. */
  readonly link: WorldLink | null;
  private flagCount = -1;
  /** Refused edits by reason (stale, reach, rate, nosim, ...), for diagnostics. */
  readonly refused: Record<string, number> = {};
  /** Diagnostics counters (debug HUD / tests). */
  readonly stats = { rejected: 0, corrected: 0, badSig: 0, kicked: 0, migrations: 0 };

  constructor(
    readonly me: Identity,
    readonly transport: Transport,
    readonly game: GameAdapter,
    readonly opts: SessionOptions,
    readonly ev: SessionEvents = {},
  ) {
    this.now = opts.now ?? (() => performance.now());
    this.link = opts.link?.usable ? opts.link : null;
    this.hostTimeout = opts.hostTimeoutMs ?? 1500;
    this.startedAt = this.now();
    this.hbEvery = opts.heartbeatMs ?? 250;
    this.role = opts.create ? 'host' : 'client';
    this.worldId = opts.worldId ?? '';
    this.selfMember = { id: me.id, name: sanitizeName(opts.name), pub: toB64(me.pub), epoch: 0, slot: 0 };
    if (opts.create) {
      this.term = 1;
      if (opts.resume) {
        this.log.resetTo(opts.resume.lastSeq, opts.resume.lastTerm, opts.resume.bounds);
        this.term = opts.resume.lastTerm + 1;
      }
      this.hostId = me.id;
      this.members.set(me.id, this.selfMember);
      this.status = 'live';
    }
    game.setReplica(!opts.create);
    transport.onPeerJoin = (t) => { this.sayHello(t); };
    transport.onPeerLeave = (t) => this.onTransportLeave(t);
    transport.onMessage = (from, ch, data) => this.onMessage(from, ch, data);
    for (const t of transport.peers()) this.sayHello(t);
    this.link?.attach({
      blocks: (rows) => this.onServerBlocks(rows),
      revert: (d, x, y, z, id, why) => {
        this.stats.rejected++;
        this.refused[why] = (this.refused[why] ?? 0) + 1;
        if (this.stats.rejected <= 20) console.info(`[net] world server refused an edit at ${x},${y},${z} (${why}); block restored to ${id}`);
        this.applyServerBlock(d, x, y, z, id);
      },
      flags: (flags) => { for (const f of flags) this.game.addWorldFlag?.(f); },
    });
  }

  get isHost(): boolean { return this.role === 'host'; }
  /** true when the room's world server, not a browser, owns the blocks. */
  get srv(): boolean { return this.link !== null && this.link.usable; }
  /** The name the room knows this player by (the host may suffix a taken one). */
  get name(): string { return this.members.get(this.me.id)?.name ?? this.selfMember.name; }

  // ================================================================ public API

  /** Drive with ~20 Hz (the game installs a worker-backed timer). */
  start(intervalMs = 50, timerFactory?: (cb: () => void, ms: number) => () => void): void {
    const make = timerFactory ?? ((cb, ms) => { const h = setInterval(cb, ms); return () => clearInterval(h); });
    this.timer = make(() => this.tick(), intervalMs);
  }

  leave(): void {
    if (this.status === 'closed') return;
    this.status = 'closed';
    this.timer?.();
    this.timer = null;
    this.link?.detach();
    this.transport.close();
    this.ev.status?.('closed');
  }

  /** The local player changed a block (player action). */
  localEdit(x: number, y: number, z: number, id: number, prev: number, fromPlayer: boolean): void {
    if (this.status === 'closed') return;
    if (this.link && this.srv) {
      // the server decides; only the sim role may speak for the simulation
      if (fromPlayer) {
        const st = this.game.localState(this.tickN);
        this.link.edit(this.game.dim?.() ?? 0, x, y, z, id, prev, false, [st.x, st.y, st.z]);
      } else if (this.isHost) {
        this.link.edit(this.game.dim?.() ?? 0, x, y, z, id, prev, true);
      }
      return;
    }
    if (this.isHost) {
      this.batch.push(this.log.next(this.term, x, y, z, id, prev, fromPlayer ? this.me.id : ''));
      return;
    }
    if (!fromPlayer) return; // client-side sims are cosmetic; the host's sim is authoritative
    const r = this.reqSeq++;
    this.pending.set(r, { r, x, y, z, id, prev, t: this.now() });
    this.sendToHost('edit_req', { r, x, y, z, id, prev });
  }

  /** The local player hit a replicated mob (client) — host applies it. */
  hitMob(mobId: number, dmg: number, fromX: number, fromZ: number, kb: number): void {
    if (this.isHost) return;
    this.sendToHost('hit', { m: mobId, d: dmg, fx: fromX, fz: fromZ, kb });
  }

  /**
   * The local player right-clicked a replicated mob holding `held` (client):
   * the host runs it and answers through `ev.mobUsed`. Returns the request id.
   */
  useMob(mobId: number, held: Stack): number {
    const r = ++this.useSeq;
    if (!this.isHost) this.sendToHost('use', { r, m: mobId, i: held.id, c: held.count, d: held.dmg, x: stackExtras(held) });
    return r;
  }

  /** The local player swung at another player (client) — host applies it. */
  hitPlayer(targetId: string, dmg: number, fromX: number, fromZ: number, kb: number): void {
    if (this.isHost) return;
    this.sendToHost('phit', { p: targetId, d: dmg, fx: fromX, fz: fromZ, kb });
  }

  chat(text: string): void {
    const t = sanitizeChat(text);
    if (!t) return;
    if (this.isHost) {
      this.ev.chat?.(this.name, t, false);
      this.sendCtrl(null, 'chat', { by: this.me.id, name: this.name, text: t });
    } else {
      this.sendToHost('chat_req', { text: t });
    }
  }

  /**
   * Rename this player in a live session (the Options name field). The members
   * table is the single source of truth for names — chat, the player list and
   * the nametags all read it — so the host re-broadcasts it, and a client asks
   * the host to do the same on its behalf.
   */
  setName(name: string): void {
    const n = sanitizeName(name);
    if (n === this.selfMember.name) return;
    // selfMember holds the name we ask for; the table entry everyone displays
    // only changes once the host has applied it (tickRename delivers it)
    this.selfMember = { ...this.selfMember, name: n };
    this.nameDirty = true;
    this.renameAt = -Infinity;
    this.renameTries = 0;
  }

  /**
   * Deliver a pending rename. A request can be lost (host migrating, rate
   * limit), so a client repeats it until the host acks with 'renamed'; a new
   * host gets it again, since its table may predate the change.
   */
  private tickRename(now: number): void {
    if (!this.nameDirty || this.status !== 'live') return;
    if (this.isHost) {
      this.nameDirty = false;
      this.applyName(this.me.id, this.selfMember.name);
      return;
    }
    if (now - this.renameAt < 1500) return;
    // an older host never acks: stop asking rather than nag it for the whole session
    if (++this.renameTries > 8) { this.nameDirty = false; return; }
    this.renameAt = now;
    this.sendToHost('rename', { name: this.selfMember.name });
  }

  /** Host: `want`, or `want` with a number appended when another member has it. */
  private freeName(want: string, id: string): string {
    const taken = new Set<string>();
    for (const m of this.members.values()) if (m.id !== id) taken.add(m.name.toLowerCase());
    if (!taken.has(want.toLowerCase())) return want;
    for (let i = 2; ; i++) {
      const n = want.slice(0, 16 - String(i).length) + i;
      if (!taken.has(n.toLowerCase())) return n;
    }
  }

  /** Host: rename a member in the table and tell the room. false = no change. */
  private applyName(id: string, want: string): boolean {
    const mem = this.members.get(id);
    if (!mem) return false;
    const n = this.freeName(want, id);
    if (n === mem.name) return false;
    this.members.set(id, { ...mem, name: n });
    this.sendMembers();
    this.systemChat(`${mem.name} is now known as ${n}`);
    this.ev.members?.();
    return true;
  }

  /** Host: the name a peer plays under (its hello only carries the one it asked for). */
  private shownName(p: RemotePeer): string { return this.members.get(p.id)?.name ?? p.name; }

  kick(id: string, reason = 'Kicked by host'): void {
    if (!this.isHost || id === this.me.id) return;
    this.stats.kicked++;
    this.sendCtrl(null, 'kick', { id, reason });
    this.removeMember(id);
  }

  /** Remote players for rendering, interpolated INTERP_DELAY_MS behind. */
  remotePlayers(out: RemotePlayerView[] = []): RemotePlayerView[] {
    out.length = 0;
    const rt = this.now() - INTERP_DELAY_MS;
    for (const p of this.peers.values()) {
      if (!p.latest || !this.members.has(p.id)) continue;
      const v = new Float64Array(PLAYER_INTERP_WIDTH);
      if (!p.buf.sample(rt, v)) continue;
      // the members table, not the peer's own hello: a rename has to reach the
      // nametags of everyone already in the room, not just the next joiner
      out.push({
        id: p.id, name: this.members.get(p.id)!.name, state: p.latest, v, ping: p.ping, isHost: p.id === this.hostId,
      });
    }
    return out;
  }

  memberList(): Array<Member & { ping: number; isHost: boolean; isMe: boolean }> {
    return hostOrder(this.members.values()).map((m) => ({
      ...m, ping: m.id === this.me.id ? (this.isHost ? 0 : this.myPing) : (this.peers.get(m.id)?.ping ?? 0),
      // server mode: nobody hosts the world, so nobody wears the [Host] tag
      isHost: !this.srv && m.id === this.hostId, isMe: m.id === this.me.id,
    }));
  }

  // ============================================================== sending

  private sendCtrl(to: string | null, t: string, body: Record<string, unknown>): Promise<void> {
    const msg = { ...body, t, from: this.me.id, n: ++this.outN, term: this.term, sid: this.sid };
    const job = async (): Promise<void> => {
      if (this.status === 'closed' && t !== 'bye') return;
      const bytes = utf8(JSON.stringify(msg));
      const sig = await this.me.sign(bytes);
      this.transport.send(to, 'rel', encodeCtrl(bytes, sig));
    };
    this.outQ = this.outQ.then(job, job);
    return this.outQ;
  }

  private sendRaw(to: string | null, ch: Channel, data: Uint8Array): void {
    // raw sends must not overtake queued signed messages on 'rel'
    if (ch === 'rel') { this.outQ = this.outQ.then(() => this.transport.send(to, ch, data)); return; }
    this.transport.send(to, ch, data);
  }

  private tidOf(id: string): string | null { return this.peers.get(id)?.tid ?? null; }

  private sendToHost(t: string, body: Record<string, unknown>): void {
    const tid = this.tidOf(this.hostId);
    if (tid) this.sendCtrl(tid, t, body);
  }

  private sayHello(tid: string): void {
    this.sendCtrl(tid, 'hello', {
      pub: toB64(this.me.pub), name: this.selfMember.name, code: this.opts.code, worldId: this.worldId,
      srv: this.srv ? 1 : 0, role: this.role, hostId: this.hostId, synced: (this.status === 'live' || this.status === 'migrating') && !this.resyncing,
      size: this.members.size, lastSeq: this.log.lastSeq, wgv: this.game.worldgenVersion, seed: this.game.seed,
    });
  }

  // ============================================================ receiving

  private onMessage(from: string, ch: Channel, data: Uint8Array): void {
    if (this.status === 'closed' || data.length === 0 || data.length > 512 * 1024) return;
    const kind = data[0];
    if (ch === 'rel' && (kind === K_CTRL || kind === K_BLOB)) {
      // keep per-sender order through the async signature check
      const prev = this.inQ.get(from) ?? Promise.resolve();
      const next = prev.then(() => this.onRel(from, data)).catch((e) => { console.warn('[net] rel message dropped', e); });
      this.inQ.set(from, next);
      return;
    }
    const id = this.tToId.get(from);
    if (!id) return;
    const peer = this.peers.get(id);
    if (!peer) return;
    peer.lastHeard = this.now();
    try {
      switch (kind) {
        case K_PSTATE: if (this.isHost) this.onPState(peer, decodePState(data)); break;
        case K_PLAYERS: if (id === this.hostId) this.onPlayers(decodePlayers(data)); break;
        case K_ENTS: if (id === this.hostId && !this.isHost) this.game.applyEntities(decodeEntities(data), this.now()); break;
        case K_HBEAT: this.onHeartbeat(peer, decodeHeartbeat(data)); break;
        case K_PONG: {
          const p = decodePong(data);
          if (this.isHost && p.sentAt > 0) peer.ping = Math.max(0, this.now() - p.sentAt);
          break;
        }
        default: break;
      }
    } catch {
      // malformed binary: drop (schema/size checks live in the decoders)
    }
  }

  private async onRel(from: string, data: Uint8Array): Promise<void> {
    if (data[0] === K_BLOB) { await this.onBlobPart(from, data); return; }
    const { sig, body, msg } = decodeCtrl(data);
    let peer: RemotePeer | undefined;
    if (msg.t === 'hello') {
      const pub = fromB64(String(msg.pub));
      if (await idFromPub(pub) !== msg.from || !(await verify(pub, sig, body))) { this.stats.badSig++; return; }
      if (msg.from === this.me.id) {
        // two tabs of one browser landed on the same identity (identitySlot() finds
        // a free slot through the Web Locks API, which a non-secure origin, e.g. a
        // lan ip, does not have). The two can never be peers, and every binary
        // packet from the twin is dropped below for the same reason.
        console.warn('[net] a peer is using this tab\'s identity: two tabs of one browser are the same player here');
        return;
      }
      peer = this.peers.get(msg.from);
      if (!peer) {
        peer = this.newPeer(msg.from, sanitizeName(msg.name), pub, from);
        this.peers.set(msg.from, peer);
      }
      peer.tid = from;
      peer.name = sanitizeName(msg.name);
      if (msg.sid !== peer.sid) { peer.sid = String(msg.sid); peer.lastN = 0; }
      this.tToId.set(from, msg.from);
    } else {
      const id = this.tToId.get(from);
      if (!id || id !== msg.from) return; // identity not bound to this link
      peer = this.peers.get(id);
      if (!peer || msg.sid !== peer.sid || !(await verify(peer.pub, sig, body))) { this.stats.badSig++; return; }
    }
    if (msg.n <= peer.lastN) return; // replay
    peer.lastN = msg.n;
    peer.lastHeard = this.now();
    this.handleCtrl(peer, msg);
  }

  private newPeer(id: string, name: string, pub: Uint8Array, tid: string): RemotePeer {
    const now = this.now();
    return {
      id, name, pub, tid, lastN: 0, sid: '', lastHeard: now, accepted: null, acceptedAt: now,
      edits: new RateLimiter(20, 30, now), breaks: new RateLimiter(12, 20, now),
      chats: new RateLimiter(5, 5, now), hits: new RateLimiter(10, 10, now),
      renames: new RateLimiter(1, 3, now),
      violations: new Violations(), lastCorrection: 0, ping: 0,
      entSig: new Map(), entTick: 0,
      buf: new SnapshotBuffer(PLAYER_INTERP_WIDTH, PLAYER_ANGLES), latest: null, latestAt: now,
    };
  }

  private handleCtrl(p: RemotePeer, m: Ctrl): void {
    const fromHost = p.id === this.hostId;
    switch (m.t) {
      case 'hello': this.onHello(p, m); break;
      case 'members': if (fromHost || (m.term > this.term)) this.onMembers(p, m); break;
      case 'snap': if (fromHost) this.onSnapMeta(m); break;
      case 'edits': if (fromHost) this.onEdits(m); break;
      case 'edit_rej': if (fromHost) this.onEditRej(m); break;
      case 'edit_req': if (this.isHost) this.onEditReq(p, m); break;
      case 'log_req': if (this.isHost) this.onLogReq(p, m); break;
      case 'chat_req': if (this.isHost) this.onChatReq(p, m); break;
      case 'rename': if (this.isHost) this.onRename(p, m); break;
      case 'renamed': if (fromHost && m.name === this.selfMember.name) this.nameDirty = false; break;
      case 'chat': if (fromHost) { const t = sanitizeChat(m.text); if (t) this.ev.chat?.(sanitizeName(m.name), t, !!m.sys); } break;
      case 'hit': if (this.isHost) this.onHit(p, m); break;
      case 'use': if (this.isHost) this.onUse(p, m); break;
      case 'use_ack': if (fromHost) this.onUseAck(m); break;
      case 'phit': if (this.isHost) this.onPlayerHit(p, m); break;
      case 'correct': if (fromHost) this.onCorrect(m); break;
      case 'pings': if (fromHost) this.onPings(m); break;
      case 'kick': if (fromHost) this.onKick(m); break;
      case 'claim': this.onClaim(p, m); break;
      case 'digest': if (this.isHost) this.onDigest(p, m); break;
      case 'log_push': if (this.isHost) this.onLogPush(m); break;
      case 'bye': this.onTransportLeave(p.tid); break;
      default: break;
    }
  }

  // ============================================================== join flow

  private onHello(p: RemotePeer, m: Ctrl): void {
    if (this.isHost) {
      if (this.members.has(p.id)) {
        // a reloaded tab may come back under a new name
        if (!this.applyName(p.id, p.name)) this.sendMembers();
        if (!m.synced) void this.sendSnapshot(p); // the same player reloaded its tab
        return;
      }
      if (m.wgv !== this.game.worldgenVersion || m.seed !== this.game.seed) {
        this.sendCtrl(p.tid, 'kick', { id: p.id, reason: 'Incompatible game version (worldgen/seed mismatch)' });
        return;
      }
      if (this.members.size >= 10) { this.sendCtrl(p.tid, 'kick', { id: p.id, reason: 'Room is full (10 players)' }); return; }
      if ((m.srv === 1) !== this.srv) {
        // one of us could not reach the world server: our worlds are not the same world
        this.sendCtrl(p.tid, 'kick', { id: p.id, reason: 'World server unreachable for one of the players' });
        return;
      }
      if (m.role === 'host') {
        // two hosts found each other (e.g. both started alone in the shared world):
        // the bigger room wins, then the longer edit log, then the lower id
        const theirs = [Number(m.size) || 1, Number(m.lastSeq) || 0];
        const mine = [this.members.size, this.log.lastSeq];
        const theyWin = theirs[0] !== mine[0] ? theirs[0] > mine[0] : theirs[1] !== mine[1] ? theirs[1] > mine[1] : p.id < this.me.id;
        if (theyWin) {
          this.stepDownTo(p, m.term);
          return;
        }
      }
      const member: Member = {
        id: p.id, name: this.freeName(p.name, p.id), pub: toB64(p.pub), epoch: nextEpoch(this.members.values()), slot: freeSlot(this.members.values()),
      };
      this.members.set(p.id, member);
      this.sendMembers();
      void this.sendSnapshot(p);
      this.systemChat(`${member.name} joined the game`);
      this.ev.members?.();
      return;
    }
    // client: learn who the host is from any hello (highest term wins)
    if (m.role === 'host' && (this.hostId === '' || m.term > this.term)) {
      this.hostId = p.id;
      this.term = m.term;
      if (this.status === 'connecting') this.setStatus('syncing', 'Joining world...');
    } else if (this.hostId === '' && typeof m.hostId === 'string' && m.hostId && m.term >= this.term) {
      this.term = m.term;
      this.hostId = m.hostId;
    }
  }

  private sendMembers(): void {
    this.sendCtrl(null, 'members', { hostId: this.hostId, list: [...this.members.values()] });
  }

  private onMembers(p: RemotePeer, m: Ctrl): void {
    if (!Array.isArray(m.list)) return;
    if (m.term > this.term) this.follow(p.id, m.term); // a newer host exists: step down / repoint
    const next = new Map<string, Member>();
    for (const raw of m.list as Member[]) {
      if (!raw || typeof raw.id !== 'string' || typeof raw.pub !== 'string') continue;
      next.set(raw.id, { id: raw.id, name: sanitizeName(raw.name), pub: raw.pub, epoch: raw.epoch | 0, slot: raw.slot | 0 });
    }
    if (!next.has(this.me.id) && this.status === 'live') return; // stale list
    for (const id of [...this.members.keys()]) if (!next.has(id)) this.ev.playerLeft?.(id);
    this.members.clear();
    for (const [id, mem] of next) this.members.set(id, mem);
    this.ev.members?.();
  }

  private async sendSnapshot(p: RemotePeer): Promise<void> {
    // server mode: the joiner already has the world from the server, so the
    // snapshot only carries the join handshake (time, where to stand)
    const quads = this.srv ? new Int32Array(0) : this.game.exportDelta();
    const tail = this.srv ? [] : this.log.since(this.log.firstSeq).map(toTuple);
    const meta = utf8(JSON.stringify({
      lastSeq: this.log.lastSeq, lastTerm: this.log.lastTerm, bounds: this.log.bounds, firstSeq: this.log.firstSeq, tail,
    }));
    const raw = new Writer(meta.length + quads.length * 4 + 16).u32(meta.length).bytes(meta).bytes(packQuads(quads)).done();
    const z = await deflate(raw);
    const hash = toB64(await sha256(z));
    const blobId = this.blobSeq++;
    const parts = encodeBlobParts(blobId, z);
    const me = this.members.get(this.me.id);
    const pos = this.game.localState(this.tickN);
    this.sendCtrl(p.tid, 'snap', {
      blobId, parts: parts.length, hash, size: z.length, worldId: this.worldId, seed: this.game.seed, wgv: this.game.worldgenVersion,
      time: this.game.time(), spawn: [pos.x, pos.y, pos.z], hostSlot: me?.slot ?? 0, srv: this.srv ? 1 : 0,
    });
    for (const part of parts) this.sendRaw(p.tid, 'rel', part);
  }

  private onSnapMeta(m: Ctrl): void {
    const n = Number(m.parts);
    if (!Number.isInteger(n) || n < 1 || n > 4096) return;
    if (m.seed !== this.game.seed || m.wgv !== this.game.worldgenVersion) {
      this.setStatus('closed', 'Incompatible world (seed / worldgen version differ)');
      return;
    }
    this.blob = { id: Number(m.blobId), n, parts: new Array(n), got: 0, meta: m };
    this.setStatus('syncing', 'Downloading world...');
  }

  private async onBlobPart(from: string, data: Uint8Array): Promise<void> {
    const id = this.tToId.get(from);
    if (!id || id !== this.hostId || !this.blob) return;
    const part = decodeBlobPart(data);
    const b = this.blob;
    if (part.blobId !== b.id || part.n !== b.n || b.parts[part.idx]) return;
    b.parts[part.idx] = part.part;
    if (++b.got < b.n) return;
    this.blob = null;
    let size = 0;
    for (const x of b.parts) size += x.length;
    const z = new Uint8Array(size);
    let o = 0;
    for (const x of b.parts) { z.set(x, o); o += x.length; }
    if (toB64(await sha256(z)) !== b.meta.hash) { this.setStatus('syncing', 'World download corrupt, retrying'); this.sendToHost('log_req', { from: 0 }); return; }
    await this.applySnapshot(b.meta, await inflate(z));
  }

  private async applySnapshot(meta: Ctrl, raw: Uint8Array): Promise<void> {
    const r = new Reader(raw);
    const ml = r.u32();
    const info = JSON.parse(fromUtf8(r.bytes(ml))) as { lastSeq: number; lastTerm: number; bounds: Bounds; firstSeq: number; tail: unknown[] };
    const quads = unpackQuads(r.bytes(r.left));
    if (!this.srv) {
      this.game.importDelta(quads);
      const tail = info.tail.map(fromTuple).filter((e): e is Edit => e !== null);
      const start = tail.length ? tail[0].seq - 1 : info.lastSeq;
      const startTerm = tail.length ? (info.bounds.filter((b) => b[1] <= start).pop()?.[0] ?? 0) : info.lastTerm;
      this.log.resetTo(start, startTerm, info.bounds.filter((b) => b[1] <= start));
      for (const e of tail) this.log.append(e);
    }
    this.worldId = String(meta.worldId ?? '');
    if (typeof meta.time === 'number') this.game.setTime(meta.time);
    // A joiner is not moved to the host: everyone spawns by the same rule
    // (game/spawn.ts), around the world spawn point.
    const buffered = this.bufferedEdits;
    this.bufferedEdits = [];
    this.mergeEdits(buffered);
    this.resyncing = false;
    this.setStatus('live');
  }

  // ================================================================ edits

  private onEdits(m: Ctrl): void {
    if (this.srv) return; // blocks come from the world server, never from a peer
    const list = Array.isArray(m.e) ? (m.e as unknown[]).map(fromTuple).filter((e): e is Edit => e !== null) : [];
    const acks = (m.acks as Record<string, number[]> | undefined)?.[this.me.id];
    if (Array.isArray(acks)) for (const r of acks) this.pending.delete(r);
    if (this.status !== 'live' && this.status !== 'migrating') { this.bufferedEdits.push(...list); return; }
    this.mergeEdits(list);
  }

  private mergeEdits(list: Edit[]): void {
    if (list.length === 0) return;
    const { undo, apply, gap } = this.log.mergeAuthoritative(list);
    this.applyMerge(undo, apply);
    if (gap > 0) this.requestLog(gap);
  }

  private applyMerge(undo: Edit[], apply: Edit[]): void {
    for (const e of undo) this.game.setBlockRemote(e.x, e.y, e.z, e.prev);
    for (const e of apply) {
      if (this.game.getBlock(e.x, e.y, e.z) !== e.id) this.game.setBlockRemote(e.x, e.y, e.z, e.id);
    }
  }

  // ========================================================= server mode

  /** One authoritative block value from the world server. */
  private applyServerBlock(dim: number, x: number, y: number, z: number, id: number): void {
    if (this.status === 'closed') return;
    if (this.game.setBlockIn) { this.game.setBlockIn(dim, x, y, z, id); return; }
    if (dim === 0 && this.game.getBlock(x, y, z) !== id) this.game.setBlockRemote(x, y, z, id);
  }

  private onServerBlocks(rows: BlockRow[]): void {
    for (const r of rows) this.applyServerBlock(r[1], r[2], r[3], r[4], r[5]);
  }

  /** Drive the server link: batching, reconnects, the sim role and its reports. */
  private tickLink(st: PlayerState): void {
    const link = this.link;
    if (!link || !this.srv) return;
    // the elected host is the one simulation; the server enforces that there is one
    if (this.isHost && this.status === 'live') link.claim(this.term); else link.release();
    link.tick([st.x, st.y, st.z]);
    if (!link.isSim) return;
    const flags = this.game.worldFlags?.() ?? [];
    const changed = flags.length !== this.flagCount;
    if (changed) this.flagCount = flags.length;
    link.meta(this.game.time(), changed && flags.length > 0 ? flags : undefined);
  }

  private requestLog(from: number): void {
    const now = this.now();
    if (now - this.lastLogReq < 500) return;
    this.lastLogReq = now;
    this.sendToHost('log_req', { from });
  }

  private onEditRej(m: Ctrl): void {
    const r = Number(m.r);
    const pe = this.pending.get(r);
    this.pending.delete(r);
    this.stats.rejected++;
    const x = Number(m.x), y = Number(m.y), z = Number(m.z), id = Number(m.id);
    if (![x, y, z, id].every(Number.isInteger)) return;
    if (id >= 0) this.game.setBlockRemote(x, y, z, id);
    else if (pe) this.game.setBlockRemote(pe.x, pe.y, pe.z, pe.prev);
  }

  private onEditReq(p: RemotePeer, m: Ctrl): void {
    const r = Number(m.r), x = Number(m.x), y = Number(m.y), z = Number(m.z), id = Number(m.id), prev = Number(m.prev);
    if (![r, x, y, z, id, prev].every(Number.isInteger) || id < 0 || id > 0xffff) return;
    if (this.srv) return; // not ours to decide: the peer talks to the world server
    const now = this.now();
    const cur = this.game.getBlock(x, y, z);
    const reject = (why: string, pts: number): void => {
      this.stats.rejected++;
      this.sendCtrl(p.tid, 'edit_rej', { r, x, y, z, id: cur >= 0 ? cur : prev, why });
      this.addViolation(p, pts, why);
    };
    if (!this.members.has(p.id)) return;
    if (!p.edits.take(now)) { reject('rate', 1); return; }
    if (id === 0 && prev !== 0 && !p.breaks.take(now)) { reject('break rate', 2); return; }
    const a = p.accepted ?? p.latest; // after a migration: last relayed state until a fresh one arrives
    if (!a || !withinReach(a.x, a.y, a.z, x, y, z)) { reject('reach', 3); return; }
    if (cur >= 0 && cur !== prev) { reject('stale', 0); return; }
    this.game.setBlockRemote(x, y, z, id);
    const e = this.log.next(this.term, x, y, z, id, cur >= 0 ? cur : prev, p.id);
    this.batch.push(e);
    let ak = this.acks.get(p.id);
    if (!ak) { ak = []; this.acks.set(p.id, ak); }
    ak.push(r);
  }

  private onLogReq(p: RemotePeer, m: Ctrl): void {
    const from = Number(m.from);
    if (!Number.isInteger(from)) return;
    if (from >= 1 && this.log.covers(from)) {
      const list = this.log.since(from);
      for (let i = 0; i < list.length; i += 512) {
        this.sendCtrl(p.tid, 'edits', { e: list.slice(i, i + 512).map(toTuple) });
      }
    } else {
      void this.sendSnapshot(p);
    }
  }

  // ================================================================= chat

  private onChatReq(p: RemotePeer, m: Ctrl): void {
    const t = sanitizeChat(m.text);
    if (!t) return;
    if (!p.chats.take(this.now())) { this.addViolation(p, 1, 'chat spam'); return; }
    const name = this.shownName(p);
    this.ev.chat?.(name, t, false);
    this.sendCtrl(null, 'chat', { by: p.id, name, text: t });
  }

  /** A client changed the name others see (Options); the host owns the table. */
  private onRename(p: RemotePeer, m: Ctrl): void {
    if (!p.renames.take(this.now())) return; // no ack: the client asks again
    const n = sanitizeName(m.name);
    p.name = n;
    this.applyName(p.id, n);
    this.sendCtrl(p.tid, 'renamed', { name: n });
  }

  private systemChat(text: string): void {
    this.ev.chat?.('', text, true);
    if (this.isHost) this.sendCtrl(null, 'chat', { by: '', name: '', text, sys: 1 });
  }

  // ================================================================ mobs

  private onUse(p: RemotePeer, m: Ctrl): void {
    const r = Number(m.r), id = Number(m.m), item = Number(m.i), count = Number(m.c), dmg = Number(m.d);
    if (![r, id, item, count, dmg].every(Number.isFinite) || item < 0 || count < 0) return;
    const ex = Array.isArray(m.x) && m.x.length <= 255 && m.x.every((v: unknown) => Number.isInteger(v)) ? m.x as number[] : undefined;
    if (!p.hits.take(this.now())) { this.addViolation(p, 1, 'use rate'); return; }
    const pos = this.game.mobPos(id);
    const a = p.accepted;
    if (!pos || !a) return;
    if (Math.hypot(pos[0] - a.x, pos[1] - a.y, pos[2] - a.z) > 7) { this.addViolation(p, 2, 'use reach'); return; }
    const held = stackWith(item | 0, Math.min(64, count | 0), dmg | 0, ex);
    if (held.name) held.name = held.name.replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 50) || undefined;
    const fx = this.game.useMob?.(id, held);
    if (fx) this.sendCtrl(p.tid, 'use_ack', { r, c: fx.consume ?? 0, w: fx.wear ?? 0, g: fx.give ?? null });
  }

  private onUseAck(m: Ctrl): void {
    const r = Number(m.r), c = Number(m.c), w = Number(m.w);
    if (![r, c, w].every(Number.isFinite)) return;
    const g = Array.isArray(m.g) && m.g.length === 3 && m.g.every((v: unknown) => Number.isInteger(v)) ? m.g as [number, number, number] : undefined;
    this.ev.mobUsed?.(r, { consume: Math.max(0, Math.min(64, c | 0)), wear: Math.max(0, Math.min(64, w | 0)), give: g });
  }

  private onHit(p: RemotePeer, m: Ctrl): void {
    const id = Number(m.m), d = Number(m.d), fx = Number(m.fx), fz = Number(m.fz), kb = Number(m.kb);
    if (![id, d, fx, fz, kb].every(Number.isFinite)) return;
    if (!p.hits.take(this.now())) { this.addViolation(p, 1, 'hit rate'); return; }
    const pos = this.game.mobPos(id);
    const a = p.accepted;
    if (!pos || !a) return;
    if (Math.hypot(pos[0] - a.x, pos[1] - a.y, pos[2] - a.z) > 7) { this.addViolation(p, 2, 'hit reach'); return; }
    this.game.hitMob(id, Math.max(0, Math.min(40, d)), fx, fz, Math.max(0, Math.min(2, kb)));
  }

  /**
   * A client swung at another player. Same gates as a mob hit (rate, reach),
   * plus: never yourself, never someone who is not in the room. Whoever owns
   * the body gets the damage back as a K_DMG, so the host's numbers are the
   * only ones that count.
   */
  private onPlayerHit(p: RemotePeer, m: Ctrl): void {
    const id = String(m.p ?? '');
    const d = Number(m.d), fx = Number(m.fx), fz = Number(m.fz), kb = Number(m.kb);
    if (!id || id === p.id) return;
    if (![d, fx, fz, kb].every(Number.isFinite)) return;
    if (!this.members.has(id)) return;
    if (!p.hits.take(this.now())) { this.addViolation(p, 1, 'hit rate'); return; }
    const pos = this.game.playerPos(id);
    const a = p.accepted;
    if (!pos || !a) return;
    if (Math.hypot(pos[0] - a.x, pos[1] - a.y, pos[2] - a.z) > 7) { this.addViolation(p, 2, 'hit reach'); return; }
    this.game.hitPlayer(id, Math.max(0, Math.min(40, d)), fx, fz, Math.max(0, Math.min(2, kb)));
  }

  // ======================================================= player state

  private onPState(p: RemotePeer, s: PlayerState): void {
    if (!this.members.has(p.id)) return;
    const now = this.now();
    const a = p.accepted;
    if (a) {
      const ticks = Math.min(Math.max(1, s.tick - a.tick), (now - p.acceptedAt) / 50 + 3);
      if (s.tick <= a.tick) return; // stale / reordered
      // a respawn is a legitimate jump: the first living state after a dead one is taken as it comes
      const respawned = (a.flags & PF_DEAD) !== 0 && (s.flags & PF_DEAD) === 0;
      if (!respawned && !plausibleMove(a.x, a.y, a.z, s.x, s.y, s.z, ticks)) {
        this.stats.corrected++;
        this.addViolation(p, 2, 'movement');
        if (now - p.lastCorrection > 500) {
          p.lastCorrection = now;
          this.sendCtrl(p.tid, 'correct', { x: a.x, y: a.y, z: a.z });
        }
        a.tick = s.tick; // keep tick moving so the next honest packet compares sanely
        p.acceptedAt = now;
        return;
      }
    }
    p.accepted = s;
    p.acceptedAt = now;
    this.pushRemote(p, s, now);
  }

  /**
   * Zero the hurt flash of peers we have not heard a state from: the flash rides
   * in the peer's own state, so a peer that stopped sending (still syncing, a
   * backgrounded tab, a paused game) would otherwise stay red forever.
   */
  expireStaleHurt(maxAgeMs: number): void {
    const now = this.now();
    for (const p of this.peers.values()) {
      if (!p.latest || p.latest.hurtTime <= 0) continue;
      if (now - p.latestAt > maxAgeMs) p.latest = { ...p.latest, hurtTime: 0 };
    }
  }

  /**
   * Announce a death the way vanilla does ("<name> died"). Peers' deaths are
   * noticed by the host off their state flags (see pushRemote); this is for the
   * host's own death, which nobody else can see. No-op in single player.
   */
  announceDeath(name = this.name): void {
    if (!this.isHost) return;
    this.systemChat(`${name} died`);
  }

  private pushRemote(p: RemotePeer, s: PlayerState, now: number): void {
    // the dead flag is the only death signal that reaches the host, and the
    // state repeats every tick, so announce the edge and nothing else
    if (this.isHost && p.latest && (s.flags & PF_DEAD) !== 0 && (p.latest.flags & PF_DEAD) === 0) {
      this.systemChat(`${this.shownName(p)} died`);
    }
    p.latest = s;
    p.latestAt = now;
    p.buf.push(now, [s.x, s.y, s.z, s.yaw, s.headYaw, s.pitch, s.limbSwing, s.limbSwingAmount, s.swing]);
  }

  private onPlayers(list: Array<{ slot: number; s: PlayerState }>): void {
    const now = this.now();
    const bySlot = new Map<number, string>();
    for (const m of this.members.values()) bySlot.set(m.slot, m.id);
    for (const { slot, s } of list) {
      const id = bySlot.get(slot);
      if (!id || id === this.me.id) continue;
      const p = this.peers.get(id);
      if (p) this.pushRemote(p, s, now);
    }
  }

  private onCorrect(m: Ctrl): void {
    const x = Number(m.x), y = Number(m.y), z = Number(m.z);
    if ([x, y, z].every(Number.isFinite)) this.game.teleport(x, y, z);
  }

  private onPings(m: Ctrl): void {
    const map = m.p as Record<string, number> | undefined;
    if (!map) return;
    for (const [id, ms] of Object.entries(map)) {
      if (id === this.me.id) this.myPing = Number(ms) || 0;
      const p = this.peers.get(id);
      if (p && id !== this.hostId) p.ping = Number(ms) || 0;
    }
  }

  private addViolation(p: RemotePeer, pts: number, why: string): void {
    if (pts <= 0) return;
    if (p.violations.add(this.now(), pts) > Violations.KICK_SCORE) this.kick(p.id, `Kicked: too many invalid actions (${why})`);
  }

  private onKick(m: Ctrl): void {
    if (m.id === this.me.id) {
      this.ev.kicked?.(String(m.reason ?? 'Kicked'));
      this.leave();
      return;
    }
    this.removeMember(String(m.id));
  }

  // ===================================================== heartbeat / tick

  private onHeartbeat(p: RemotePeer, h: ReturnType<typeof decodeHeartbeat>): void {
    if (p.id === this.hostId) {
      this.hostSilentSince = 0;
      this.sendRaw(p.tid, 'unrel', encodePong(h.sentAt, this.log.lastSeq));
      if (this.status === 'live' && h.lastSeq > this.log.lastSeq && this.pendingGapAge(h.lastSeq)) this.requestLog(this.log.lastSeq + 1);
      if (this.status === 'live' && Math.abs(this.game.time() - h.time) > 0.002) this.game.setTime(h.time);
      return;
    }
    // another peer believes it is host
    const order = hostOrder(this.members.values());
    const higher = h.term > this.term || (h.term === this.term && rankOf(order, p.id) < rankOf(order, this.hostId));
    if (higher && this.members.has(p.id)) this.follow(p.id, h.term);
  }

  private gapSince = 0;
  private pendingGapAge(hostSeq: number): boolean {
    if (hostSeq <= this.log.lastSeq) { this.gapSince = 0; return false; }
    const now = this.now();
    if (this.gapSince === 0) this.gapSince = now;
    return now - this.gapSince > 1000;
  }

  /** Lost a host-vs-host tie: join their world (they send us a snapshot). */
  private stepDownTo(p: RemotePeer, term: number): void {
    this.resyncing = true;
    this.members.clear();
    this.follow(p.id, Math.max(term, this.term));
    this.setStatus('syncing', 'Joining world...');
    this.sayHello(p.tid);
  }

  /** Auto mode: nobody hosts after the discovery window -> host alone (resuming any cached log). */
  private hostAlone(): void {
    this.role = 'host';
    // P2P mode: the room may already have a history behind us (this page's room
    // cache), so continue its numbering instead of forking it at (1, 1)
    if (this.opts.resume && this.log.lastSeq === 0 && this.log.lastTerm === 0) {
      this.log.resetTo(this.opts.resume.lastSeq, this.opts.resume.lastTerm, this.opts.resume.bounds);
    }
    this.term = Math.max(this.term, this.log.lastTerm) + 1;
    this.hostId = this.me.id;
    this.members.clear();
    this.members.set(this.me.id, this.selfMember);
    this.game.setReplica(false);
    this.setStatus('live');
    // Announce it: two tabs that pressed Play at the same moment both reach here
    // with only "client" hellos exchanged, and without this neither ever learns
    // the other is hosting (each ignores the other's broadcast for good).
    for (const t of this.transport.peers()) this.sayHello(t);
    this.ev.hostChanged?.(this.me.id, this.term);
    this.ev.members?.();
  }

  tick(): void {
    if (this.status === 'closed') return;
    this.tickN++;
    const now = this.now();
    if (this.opts.discoverMs !== undefined && this.hostId === '' && this.status === 'connecting' && now - this.startedAt > this.opts.discoverMs) {
      this.hostAlone();
    }
    const st = this.game.localState(this.tickN);
    this.tickLink(st);
    this.tickRename(now);
    if (this.isHost) {
      // flush ordered edits
      if (this.batch.length) {
        const flushed = this.batch;
        this.batch = [];
        const acks: Record<string, number[]> = {};
        for (const [k, v] of this.acks) acks[k] = v;
        for (let i = 0; i < flushed.length; i += 512) {
          this.sendCtrl(null, 'edits', { e: flushed.slice(i, i + 512).map(toTuple), acks: i === 0 ? acks : undefined });
        }
        this.acks.clear();
      }
      if (now - this.lastHb >= this.hbEvery) {
        this.lastHb = now;
        this.sendRaw(null, 'unrel', encodeHeartbeat({ term: this.term, lastSeq: this.log.lastSeq, tick: this.tickN, sentAt: now, time: this.game.time() }));
      }
      // players
      const me = this.members.get(this.me.id);
      const list: Array<{ slot: number; s: PlayerState }> = [{ slot: me?.slot ?? 0, s: st }];
      for (const p of this.peers.values()) {
        const m = this.members.get(p.id);
        if (m && p.accepted) list.push({ slot: m.slot, s: p.accepted });
      }
      if (this.peers.size) this.sendRaw(null, 'unrel', encodePlayers(list));
      this.sendEntities();
      if (now - this.lastPings > 2000) {
        this.lastPings = now;
        const p: Record<string, number> = {};
        for (const peer of this.peers.values()) if (this.members.has(peer.id)) p[peer.id] = Math.round(peer.ping);
        if (Object.keys(p).length) this.sendCtrl(null, 'pings', { p });
      }
      // drop members whose link went silent for long
      for (const p of [...this.peers.values()]) {
        if (this.members.has(p.id) && now - p.lastHeard > 10000) { this.removeMember(p.id); }
      }
    } else {
      const hostTid = this.tidOf(this.hostId);
      // not while parked above an unloaded spawn: the drop to the ground would read as an impossible move
      if (hostTid && this.status === 'live' && !this.game.holding?.()) this.sendRaw(hostTid, 'unrel', encodePState(st));
      this.checkHost(now);
      // pending edits nobody answered (lost during a migration): resend once in a while
      for (const pe of this.pending.values()) {
        if (now - pe.t > 3000 && this.status === 'live') {
          pe.t = now;
          this.sendToHost('edit_req', { r: pe.r, x: pe.x, y: pe.y, z: pe.z, id: pe.id, prev: pe.prev });
        }
      }
    }
    // everyone: 1 Hz liveness beacon to the whole mesh (standbys need it for election)
    if (now - this.lastAlive > 1000) {
      this.lastAlive = now;
      this.sendRaw(null, 'unrel', encodePong(0, this.log.lastSeq));
    }
  }

  private sendEntities(): void {
    const mobs = this.game.mobStates();
    for (const p of this.peers.values()) {
      if (!this.members.has(p.id) || !p.accepted) continue;
      const a = p.accepted;
      const keyframe = this.tickN - p.entTick >= 20;
      if (keyframe) p.entTick = this.tickN;
      const ents: EntityState[] = [];
      const seen = new Set<number>();
      for (const e of mobs) {
        if (Math.abs(e.x - a.x) > 128 || Math.abs(e.z - a.z) > 128) continue;
        seen.add(e.id);
        const sig = entSig(e);
        if (!keyframe && p.entSig.get(e.id) === sig) continue;
        p.entSig.set(e.id, sig);
        ents.push(e);
      }
      const removed: number[] = [];
      for (const id of [...p.entSig.keys()]) if (!seen.has(id)) { removed.push(id); p.entSig.delete(id); }
      if (!keyframe && ents.length === 0 && removed.length === 0) continue;
      this.sendRaw(p.tid, 'unrel', encodeEntities({ tick: this.tickN, keyframe, ents, removed }));
    }
  }

  // ======================================================== migration

  private alive = (id: string): boolean => {
    if (id === this.me.id) return true;
    const p = this.peers.get(id);
    if (!p || !this.tToId.has(p.tid)) return false;
    return this.now() - p.lastHeard < Math.max(2500, this.hostTimeout * 1.7);
  };

  private checkHost(now: number): void {
    if (this.status !== 'live' && this.status !== 'migrating') return;
    const host = this.peers.get(this.hostId);
    const silent = !host || !this.tToId.has(host.tid) || now - host.lastHeard > this.hostTimeout;
    if (!silent) { this.hostSilentSince = 0; return; }
    if (this.hostSilentSince === 0) {
      this.hostSilentSince = now;
      this.suspects = new Set([this.hostId]);
      this.setStatus('migrating', 'Host lost, reconnecting...');
    }
    const order = hostOrder(this.members.values());
    const next = successor(order, this.alive, this.suspects);
    if (next === this.me.id) { this.becomeHost(); return; }
    // the expected successor hasn't claimed in time: skip it
    if (next && now - this.hostSilentSince > this.hostTimeout * 2) {
      this.suspects.add(next);
      this.hostSilentSince = now - this.hostTimeout;
    }
  }

  private becomeHost(): void {
    this.stats.migrations++;
    const old = this.hostId;
    this.role = 'host';
    this.term += 1;
    this.hostId = this.me.id;
    this.hostSilentSince = 0;
    for (const id of this.suspects) if (id !== this.me.id) this.members.delete(id);
    this.suspects.clear();
    if (old) this.members.delete(old);
    this.game.setReplica(false);
    const now0 = this.now();
    for (const p of this.peers.values()) if (!p.accepted && p.latest) { p.accepted = p.latest; p.acceptedAt = now0; }
    // our own unconfirmed edits are now ours to order
    for (const pe of this.pending.values()) {
      const cur = this.game.getBlock(pe.x, pe.y, pe.z);
      this.batch.push(this.log.next(this.term, pe.x, pe.y, pe.z, cur >= 0 ? cur : pe.id, pe.prev, this.me.id));
    }
    this.pending.clear();
    this.sendCtrl(null, 'claim', { lastSeq: this.log.lastSeq, bounds: this.log.bounds, list: [...this.members.values()] });
    this.setStatus('live');
    this.ev.hostChanged?.(this.me.id, this.term);
    this.ev.members?.();
  }

  private onClaim(p: RemotePeer, m: Ctrl): void {
    const order = hostOrder(this.members.values());
    const current = { term: this.term, id: this.hostId };
    const forced = this.hostSilentSince > 0 && this.now() - this.hostSilentSince > this.hostTimeout * 3;
    if (!acceptClaim({ term: m.term, id: p.id }, current, order, this.alive) && !(forced && m.term > this.term)) {
      // tell a stale claimant about our (higher) term so it can step down
      if (this.isHost && m.term <= this.term) this.sendCtrl(p.tid, 'members', { hostId: this.hostId, list: [...this.members.values()] });
      return;
    }
    this.follow(p.id, m.term);
    if (Array.isArray(m.list)) this.onMembers(p, m);
    // push entries the new host may be missing
    const claimSeq = Number(m.lastSeq) | 0;
    const bounds = (Array.isArray(m.bounds) ? m.bounds : []) as Bounds;
    if (this.log.lastSeq > claimSeq && divergenceSeq(this.log, bounds, claimSeq) > claimSeq) {
      const extra = this.log.since(claimSeq + 1).map(toTuple);
      if (extra.length) this.sendCtrl(p.tid, 'log_push', { e: extra });
    }
  }

  /** Start following `hostId` at `term` (accepting a claim or stepping down). */
  private follow(hostId: string, term: number): void {
    const wasHost = this.isHost;
    this.role = 'client';
    this.term = term;
    this.hostId = hostId;
    // the new host's table may predate a rename of ours: have it confirmed again
    this.nameDirty = true;
    this.renameAt = -Infinity;
    this.renameTries = 0;
    this.hostSilentSince = 0;
    this.suspects.clear();
    if (wasHost) {
      this.game.setReplica(true);
      this.batch = [];
      this.acks.clear();
    }
    if (this.status !== 'syncing' && this.status !== 'connecting') this.setStatus('live');
    this.sendToHost('digest', { lastSeq: this.log.lastSeq, bounds: this.log.bounds });
    for (const pe of this.pending.values()) {
      pe.t = this.now();
      this.sendToHost('edit_req', { r: pe.r, x: pe.x, y: pe.y, z: pe.z, id: pe.id, prev: pe.prev });
    }
    this.ev.hostChanged?.(hostId, term);
    this.ev.members?.();
  }

  private onDigest(p: RemotePeer, m: Ctrl): void {
    const lastSeq = Number(m.lastSeq) | 0;
    const bounds = (Array.isArray(m.bounds) ? m.bounds : []) as Bounds;
    const from = divergenceSeq(this.log, bounds, lastSeq);
    if (!this.members.has(p.id)) {
      // a peer we dropped as dead (or an old host stepping down) rejoins
      this.members.set(p.id, { id: p.id, name: this.freeName(p.name, p.id), pub: toB64(p.pub), epoch: nextEpoch(this.members.values()), slot: freeSlot(this.members.values()) });
      this.sendMembers();
    }
    if (from > this.log.lastSeq) return;
    if (this.log.covers(from)) {
      const list = this.log.since(from);
      for (let i = 0; i < list.length; i += 512) this.sendCtrl(p.tid, 'edits', { e: list.slice(i, i + 512).map(toTuple) });
    } else {
      void this.sendSnapshot(p);
    }
  }

  private onLogPush(m: Ctrl): void {
    const list = Array.isArray(m.e) ? (m.e as unknown[]).map(fromTuple).filter((e): e is Edit => e !== null) : [];
    list.sort((a, b) => a.seq - b.seq);
    for (const e of list) {
      if (e.seq !== this.log.lastSeq + 1) continue;
      this.log.append(e);
      if (this.game.getBlock(e.x, e.y, e.z) !== e.id) this.game.setBlockRemote(e.x, e.y, e.z, e.id);
      this.batch.push(e);
    }
  }

  // ======================================================== membership

  private onTransportLeave(tid: string): void {
    const id = this.tToId.get(tid);
    if (!id) return;
    this.tToId.delete(tid);
    const p = this.peers.get(id);
    if (p) p.lastHeard = 0;
    if (this.isHost && this.members.has(id)) this.removeMember(id);
    else if (!this.isHost && id === this.hostId) this.checkHost(this.now());
  }

  private removeMember(id: string): void {
    const m = this.members.get(id);
    if (!m) return;
    this.members.delete(id);
    const p = this.peers.get(id);
    if (p) { p.accepted = null; p.latest = null; p.entSig.clear(); }
    this.ev.playerLeft?.(id);
    this.ev.members?.();
    if (this.isHost) {
      this.sendMembers();
      this.systemChat(`${m.name} left the game`);
    }
  }

  private setStatus(s: Status, detail?: string): void {
    this.status = s;
    this.ev.status?.(s, detail);
  }
}

function entSig(e: EntityState): number {
  // cheap change detector over quantized fields
  let h = e.flags * 31 + e.type;
  h = (h * 31 + Math.round(e.x * 32)) | 0;
  h = (h * 31 + Math.round(e.y * 32)) | 0;
  h = (h * 31 + Math.round(e.z * 32)) | 0;
  h = (h * 31 + Math.round(e.yaw * 100)) | 0;
  h = (h * 31 + Math.round(e.headYaw * 100)) | 0;
  h = (h * 31 + Math.round(e.limbSwing * 50)) | 0;
  h = (h * 31 + e.hurtTime + e.deathTime * 256) | 0;
  h = (h * 31 + Math.round(e.swell * 64) + Math.round(e.attackAnim * 64) * 128) | 0;
  if (e.name) for (let i = 0; i < e.name.length; i++) h = (h * 31 + e.name.charCodeAt(i)) | 0;
  return h;
}
